function getExtForType(type) {
  if (type === 'image/webp') return 'webp';
  if (type === 'image/png') return 'png';
  return 'jpg';
}

async function dataUrlFromCanvas(canvas, type, quality) {
  const q = typeof quality === 'number' ? quality : 0.7;
  try {
    return canvas.toDataURL(type, q);
  } catch {
    // Fallback: PNG
    return canvas.toDataURL('image/png');
  }
}

async function blobFromCanvas(canvas, preferredType, quality) {
  const q = typeof quality === 'number' ? quality : 0.82;

  const tryType = (t) =>
    new Promise((resolve) => {
      canvas.toBlob((b) => resolve(b || null), t, q);
    });

  // Try preferred, then fall back to jpeg.
  const first = await tryType(preferredType);
  if (first) return first;
  const second = await tryType('image/jpeg');
  if (second) return second;
  throw new Error('Failed to encode image.');
}

/**
 * Resize an image file/blob to a max width (keeping aspect ratio).
 * Returns a Blob ready to upload.
 */
export async function resizeImageToMaxWidth(fileOrBlob, { maxWidth, type = 'image/webp', quality = 0.82 }) {
  const src = fileOrBlob;
  if (!src) throw new Error('Missing image');

  const bmp = await createImageBitmap(src);
  const scale = Math.min(1, maxWidth / bmp.width);
  const w = Math.max(1, Math.round(bmp.width * scale));
  const h = Math.max(1, Math.round(bmp.height * scale));

  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext('2d', { alpha: false });
  if (!ctx) throw new Error('Canvas is not supported');

  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = 'high';
  ctx.drawImage(bmp, 0, 0, w, h);

  const blob = await blobFromCanvas(canvas, type, quality);
  return {
    blob,
    width: w,
    height: h,
    contentType: blob.type || type,
    ext: getExtForType(blob.type || type),
  };
}

/**
 * Create a tiny base64 data URL preview (LQIP) for immediate display.
 * This is intentionally very small to keep Firestore doc sizes safe.
 */
export async function createImageLqipDataUrl(fileOrBlob, { maxWidth = 32, quality = 0.55 } = {}) {
  const bmp = await createImageBitmap(fileOrBlob);
  const scale = Math.min(1, maxWidth / bmp.width);
  const w = Math.max(1, Math.round(bmp.width * scale));
  const h = Math.max(1, Math.round(bmp.height * scale));

  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext('2d', { alpha: false });
  if (!ctx) throw new Error('Canvas is not supported');

  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = 'high';
  ctx.drawImage(bmp, 0, 0, w, h);

  // JPEG is usually smaller than PNG for photos.
  const dataUrl = await dataUrlFromCanvas(canvas, 'image/jpeg', quality);
  return dataUrl;
}


